import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { useHistory } from 'react-router-dom';

import Heading from './Heading/Heading';
import Filters from './Filters/Filters';
import SearchFiles from './SearchFiles/SearchFiles';
import Menu from './Menu/Menu';
import { useFileManagerContext } from 'modules/file_manager/context/FileManagerContext';

function FileManagerHeader() {
  const {
    isGlobalFileManager,
    isCaseFileManager,
    isFirmDocuments,
    isClientFileManager,
    isLeadFileManager,
    modals,
    setSelectedItem,
    setVersionFileUpload,
    setFileManagerFilters,
    fileManagerFilters,
  } = useFileManagerContext();
  const history = useHistory();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detect theme changes
  useEffect(() => {
    const checkTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme');
      setIsDarkMode(theme === 'dark');
    };

    // Check initial theme
    checkTheme();

    // Listen for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => observer.disconnect();
  }, []);

  const handleFirmUpload = () => {
    setSelectedItem();
    setVersionFileUpload(false);
    modals.setIsFirmDocumentsUpload(true);
    modals.setShowFileUploadModal(true);
  };

  const chipItems = [
    { label: 'All Files', value: 'all' },
    { label: 'Client Uploads', value: 'uploads' },
    { label: 'Unread', value: 'unread' },
    { label: 'Signed Documents', value: 'signed' },
  ];

  // Update active tab index when filters change
  useEffect(() => {
    const getActiveTabIndex = () => {
      if (fileManagerFilters?.esignature_status === 'SIGNED') {
        return 3; // Signed Documents
      } else if (fileManagerFilters?.is_read === false) {
        return 2; // Unread
      } else if (fileManagerFilters?.is_client_uploaded === true || fileManagerFilters?.uploadedBy === 'client') {
        return 1; // Client Uploads
      } else {
        return 0; // All Files (default)
      }
    };

    const newActiveIndex = getActiveTabIndex();
    setActiveTabIndex(newActiveIndex);
  }, [
    fileManagerFilters?.esignature_status,
    fileManagerFilters?.is_read,
    fileManagerFilters?.is_client_uploaded,
    fileManagerFilters?.uploadedBy,
  ]);

  const handleChipClick = (chipValue, index) => {
    setActiveTabIndex(index);

    // Apply global filter based on chip selection
    setFileManagerFilters((prevValues) => {
      const newFilters = { ...prevValues };

      // Reset chip-related filters first
      newFilters.is_client_uploaded = '';
      newFilters.is_read = '';
      newFilters.esignature_status = '';
      newFilters.forceCaseUndefined = false;
      newFilters.isFirmUploads = false;

      switch (chipValue) {
        case 'all':
          // All Files - reset all filters, include case_id normally, no is_client_uploaded
          newFilters.uploadedBy = '';
          newFilters.is_client_uploaded = '';
          newFilters.forceCaseUndefined = false;
          break;

        case 'uploads':
          // Client Uploads - set is_client_uploaded to true, no case_id
          newFilters.is_client_uploaded = true;
          newFilters.uploadedBy = 'client';
          newFilters.forceCaseUndefined = false;
          break;

        case 'unread':
          // Unread - set is_read to false
          newFilters.is_read = false;
          break;

        case 'signed':
          // Signed Documents - set esignature_status to SIGNED
          newFilters.esignature_status = 'SIGNED';
          break;

        default:
          break;
      }

      return newFilters;
    });
  };

  return (
    <div className="">
      {/* Header for Firm Documents page */}
      {isFirmDocuments && (
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-2 gap-2">
          <div className="d-flex">
            <a className="text-decoration-none pointer" onClick={() => history.push('/file-manager')}>
              <div className="icon-box me-3 mt-2">
                <i className="pi pi-angle-left"></i>
              </div>
            </a>
            <div>
              <h1 className="title gray-800 mb-0 fs-3 fs-md-1" style={{ fontSize: '24px', fontWeight: '600' }}>
                Firm Documents
              </h1>
              <div className="d-flex align-items-center mb-1">
                <span className="text-muted me-2 cursor-pointer" style={{ fontSize: '14px' }} onClick={() => history.push('/file-manager')}>
                  File Manager
                </span>
                <i className="pi pi-angle-right text-muted me-2" style={{ fontSize: '12px' }}></i>
                <span style={{ fontSize: '14px', fontWeight: '500' }}>Firm Documents</span>
              </div>
            </div>
          </div>
          <Button label="Upload to Firm Documents" className="p-button p-button-primary" onClick={handleFirmUpload} />
        </div>
      )}

      {isGlobalFileManager && !isFirmDocuments && !isCaseFileManager && !isClientFileManager && !isLeadFileManager && <Heading />}

      {/* Case Files, Client Files, and Lead Files - Original staging layout */}
      {(isCaseFileManager || isClientFileManager || isLeadFileManager) && (
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-2 col-sm-12 FM-filter">
          <div className="d-flex flex-wrap flex-grow-1 align-items-center justify-content-between filter-wrap p-2 gap-2 my-3">
            <SearchFiles />
            <Filters />
          </div>
          <div className="d-flex align-items-center ms-lg-auto my-2 flex-wrap w-100 gap-2">
            <Menu />
          </div>
        </div>
      )}

      {/* Firm Documents Section - only show on main File Manager page, not case/client/lead files */}
      {isGlobalFileManager && !isFirmDocuments && !isCaseFileManager && !isClientFileManager && !isLeadFileManager && (
        <div
          className="d-flex align-items-center justify-content-between mb-3 p-3 border-round-lg gap-3 flex-wrap"
          style={{
            backgroundColor: isDarkMode ? 'rgba(45, 55, 72, 0.3)' : '#f8f9fa',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e9ecef',
            borderRadius: '8px',
          }}
        >
          <div>
            <h5 className="mb-1 text-900" style={{ fontSize: '16px', fontWeight: '600' }}>
              Firm Documents
            </h5>
            <p className="mb-0 text-600" style={{ fontSize: '14px' }}>
              Upload firm-wide documents that will be shared with the entire firm
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              label="View Firm Documents"
              className="p-button p-button-primary"
              onClick={() => {
                history.push('/file-manager/firm-documents');
              }}
            />
          </div>
        </div>
      )}

      {isGlobalFileManager && !isFirmDocuments && !isCaseFileManager && !isClientFileManager && !isLeadFileManager && (
        <div className="mb-md-3">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex gap-2 flex-wrap">
              {chipItems.map((item, index) => (
                <Button
                  key={item.value}
                  label={item.label}
                  className={`p-button p-button-sm ${activeTabIndex === index ? 'p-button-primary' : 'p-button-primary outline'}`}
                  style={{
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                  onClick={() => handleChipClick(item.value, index)}
                />
              ))}
            </div>

            <div className="d-none d-md-block" style={{ minWidth: '250px', maxWidth: '350px', flexShrink: 0 }}>
              <SearchFiles />
            </div>
            <div className="w-100 d-block d-md-none my-2">
              <SearchFiles />
            </div>
          </div>
        </div>
      )}

      {isFirmDocuments && (
        <div className="d-flex align-items-center mb-3 justify-content-between flex-wrap">
          <div className="d-flex flex-wrap align-items-center filter-wrap p-2 align-items-center justify-content-between gap-2">
            <SearchFiles />
            <Filters />
          </div>
          <div className="d-flex align-items-center gap-2 my-2">
            <Menu />
          </div>
        </div>
      )}

      {isGlobalFileManager && !isFirmDocuments && !isCaseFileManager && !isClientFileManager && !isLeadFileManager && (
        <>
          <div className="mb-4">
            <Filters />
          </div>

          <div className="d-flex align-items-center justify-content-end w-100 mt-2">
            <Menu />
          </div>
        </>
      )}
    </div>
  );
}

export default FileManagerHeader;
